"use client";

import { useEffect, useState } from "react";
import {
  BenefitsSection,
  FAQSection,
  Header,
  JoinTheFamSection,
  RecentDealsSection,
  ReviewsSection,
  SponsoredBannersProvider,
} from "@/components";
import type { Store } from "@/lib/types/stores";
import DankLifestyleSection from "@/components/sections/DankLifestyleSection";
import HolyShitDealsSection from "@/components/sections/HolyShitDealsSection";
import ProductShowcaseSection from "@/components/sections/ProductShowcaseSection";
import SelectDeliveryMenuSection from "@/components/sections/SelectDeliveryMenuSection";
import { PageShowcasesProvider } from "@/components/providers/PageShowcasesProvider";
import {
  DankClubComparisonSection,
  DankClubHeroSection,
  DankClubHowToSection,
} from "@/components/sections/dank-club";
import BellIcon from "@/components/icons/BellIcon";
import SaleIcon from "@/components/icons/SaleIcon";
import CreditCardIcon from "@/components/icons/CreditCardIcon";
import TagIcon from "@/components/icons/TagIcon";
import MerchIcon from "@/components/icons/MerchIcon";
import GiftsIcon from "@/components/icons/GiftsIcon";
import {
  DANK_CLUB_DEALS_SPONSORED_POSITION,
  DANK_CLUB_FAQ_SPONSORED_POSITION,
} from "@/lib/sponsored-positions";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";

const memberBenefits = [
  {
    icon: <BellIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Fresh Drop Alerts",
    description: "Be the first to know about new arrivals and exclusive product launches.",
  },
  {
    icon: <SaleIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "5% Off Always",
    description: "Enjoy a consistent 5% discount on eligible products every time you shop.",
  },
  {
    icon: <CreditCardIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Earn Points Each Visit",
    description: "Collect loyalty points on every purchase and redeem them for rewards.",
  },
  {
    icon: <TagIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Special Curated Offers",
    description: "Access personalized deals and bundles hand-picked just for members.",
  },
  {
    icon: <MerchIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Exclusive Dank Merch",
    description: "Get priority access to limited-edition apparel and branded accessories.",
  },
  {
    icon: <GiftsIcon size={24} className="w-6 h-6 text-blue-80" />,
    title: "Birthday Rewards",
    description: "Celebrate your special day with exclusive birthday gifts and bonus points.",
  },
];

export default function DankClubPage() {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/stores`, { cache: "no-store" });
        if (res.ok) {
          const data = await res.json();
          if (data?.stores) setStores(data.stores);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  return (
    <SponsoredBannersProvider>
      <PageShowcasesProvider endpoint="/api/dank-club/product-showcases">
        <div className="min-h-screen w-full min-w-0 max-w-none">
          <Header stores={stores} base={API_BASE} />

          <main className="home-page w-full max-w-none pb-16 py-4 sm:py-6">
            <div className="w-full max-w-none space-y-16 px-4 sm:space-y-36 sm:px-8 [&>section]:!px-0">
              <DankClubHeroSection />

              <SelectDeliveryMenuSection />

              <DankClubComparisonSection />

              <RecentDealsSection
                className="!px-0"
                sponsoredPosition={DANK_CLUB_DEALS_SPONSORED_POSITION}
                bannerHeading="VIP Drops & Member Deals"
                bannerDescription="Get first access to fresh product drops, exclusive member-only discounts, and entry into our special giveaways and promotions"
                bannerCtaText="View All Perks"
                bannerCtaHref="#dank-member-benefits"
              />

              <DankClubHowToSection />

              {/* Same grid rendering as the homepage showcases (not the Why Dank carousel). */}
              <ProductShowcaseSection slotKey="productSelection" className="!px-0" />

              <BenefitsSection
                id="dank-member-benefits"
                heading="Dank member benefits"
                benefits={memberBenefits}
                showShopButtons={false}
                contentClassName="!px-0"
              />

              <JoinTheFamSection
                heading="Unlock Your Welcome Gift Now!"
                description="Join the Dank Club today and receive an exclusive 20% discount on your first in-store visit. It's our way of welcoming you to the community"
                leadFormSource="dank-club"
              />

              <DankLifestyleSection apiPath="/api/homepage/merch" />

              <ProductShowcaseSection slotKey="memberPicks" className="!px-0" />

              <ReviewsSection />

              <FAQSection sponsoredPosition={DANK_CLUB_FAQ_SPONSORED_POSITION} />

              <HolyShitDealsSection />
            </div>
          </main>
        </div>
      </PageShowcasesProvider>
    </SponsoredBannersProvider>
  );
}
